/* eslint-disable camelcase */
import { useUsercentrics } from './custom/useUsercentrics/useUsercentrics';
import { orderGetters, paymentGetters } from './getters';
import type { ProductTeaser } from '~/components/Product/types/product';
import type {
  LineItem,
  Product,
  Category,
  Cart,
  Order,
  ProductVariant,
} from '~/types/GraphQL';

const PAYMENT_MAP: { [key: string]: string } = {
  pay_later: 'Invoice',
  sofort: 'Sofort',
  ideal: 'iDeal',
  paypal: 'Paypal',
  paypal_express: 'Paypal express',
  eps: 'EPS',
  p24: 'p24',
  payu: 'payu',
  vorkasse: 'Payment in advance',
  card: 'Payment with credit card',
  kik_filiale: 'Payment in store',
  kik_filiale_popup: 'Payment in store',
};

type Price = { currency: string; value: number };

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace GA4 {
  export type Event =
    // https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm
    | { event: 'view_item_list' | 'view_slider' | 'select_item'; ecommerce: Ecommerce & { item_list_id?: string; item_list_name: string; search_term?: string; filters?: { RecommenderClientId: string; ItemIds: string[] } } }
    | { event: 'view_item' | 'add_to_cart' | 'add_to_wishlist' | 'view_cart' | 'remove_from_cart' | 'begin_checkout'; ecommerce: Ecommerce & Price }
    | { event: 'add_payment_info'; ecommerce: Ecommerce & { payment_type: string } }
    | { event: 'purchase' | 'refund'; ecommerce: Ecommerce & Price & { transaction_id: string; tax: number; shipping: number } }
    | { event: 'view_promotion' | 'select_promotion'; ecommerce: Ecommerce & { creative_name: string; creative_slot: string; promotion_id: string; promotion_name: string } }
    | CustomType;

  export type CustomType =
    | { event: 'search'; searchTerm: string }
    | { event: 'error'; errorType: string; errorMessage: string }
    | { event: 'formSubmit'; formId: string }
    | { event: 'registration' }
  ;

  interface Ecommerce {
    transaction_id?: string;
    value?: number;
    tax?: number;
    shipping?: number;
    shipping_tier?: string;
    payment_type?: string;
    currency?: string;
    coupon?: string;
    coupon_key?: string;
    items: EcommerceItem[];
  }

  export interface EcommerceItem {
    item_id: string;
    item_name: string;
    affiliation?: string;
    coupon?: string;
    discount?: number;
    index: number;
    item_brand?: string;
    item_category?: string;
    item_category2?: string;
    item_category3?: string;
    item_category4?: string;
    item_category5?: string;
    item_list_id?: string;
    item_list_name?: string;
    item_variant?: string;
    location_id?: string;
    item_product?: string; // product key
    price: number;
    quantity: number;
  }
}

const getPaymentMethod = (order: Order) => {
  const method = paymentGetters.getActivePaymentMethod(order.paymentInfo)?.paymentMethodInfo?.method;

  return method ? PAYMENT_MAP[method.toLowerCase()] : null;
};

export const useGA4 = () => {
  const { $gtm, $domain } = useNuxtApp();
  const { isFunctionalConsentGiven } = useUsercentrics();

  // We need to come back here when we define better types
  const transformWeirdProduct = (product: any, index: number): GA4.EcommerceItem => {
    const shopNavigationCategories = product.categories?.find((cat: Category) => cat.ancestors?.some(ancestor => ancestor?.key?.startsWith('ShopNavigation')));
    let itemCategories;
    if (shopNavigationCategories) {
      itemCategories = shopNavigationCategories?.ancestors?.reduce((acc: any, item: any, index: any) => {
        acc[`category${index}`] = item.name;
        return acc;
      }, {} as Record<string, string>);

      itemCategories[`category${shopNavigationCategories?.ancestors?.length}`] = shopNavigationCategories?.name;

      delete itemCategories.category0;
    }
    return {
      index,
      item_name: product.name,
      price: product.price.final ?? 0,
      quantity: product.quantity ?? 1,
      item_id: product.key,
      item_group: product.attributes.ekb,
      item_ekb: product.attributes.wgr,
      item_color: product.attributes.color,
      discount: product.price.strike ? (product.price.strike - product.price.final).toFixed(2) : 0,
      item_brand: product.brandName,
      item: 'product',
      ...(itemCategories ?? {}),
    };
  };

  const transform = (product: LineItem, index: number): GA4.EcommerceItem => {
    const findAttr = (name: string) => product.variant?.attributesRaw?.find(x => x.name === name)?.value;
    const lineItemBreadcrumbs = product.custom?.customFieldsRaw?.find(({ name }) => name === 'categoryPath')?.value?.split(' / ') ?? [];

    const itemCategories = lineItemBreadcrumbs?.reduce((acc: any, item: any, index: any) => {
      acc[`category${index + 1}`] = item;
      return acc;
    }, {} as Record<string, string>);

    return {
      index,
      item_name: product.name!,
      price: (product.price?.value?.centAmount ?? 0) / 100,
      quantity: product.quantity,
      item_id: product.productKey,
      item_color: findAttr('color')?.label[$domain.locales[0]],
      item_group: findAttr('wgr'),
      item_ekb: findAttr('ekb'),
      item_product: product.productKey,
      item_variant: findAttr('color_number'),
      ...itemCategories,
    };
  };

  // We need to come back here when we define better types
  const transformToWishlist = (product: any, index: number): GA4.EcommerceItem => {
    let variant = product;
    if (product.variants) {
      variant = product.variants.find(({ sku }: ProductVariant) => product.masterVariantId === sku);
    }
    const shopNavigationCategories = product.categories?.find((cat: Category) => cat.ancestors?.some(ancestor => ancestor?.key?.startsWith('ShopNavigation')));

    const itemCategories = shopNavigationCategories?.ancestors?.reduce((acc: any, item: any, index: any) => {
      acc[`category${index}`] = item.name;
      return acc;
    }, {} as Record<string, string>);

    if (shopNavigationCategories) {
      itemCategories[`category${shopNavigationCategories?.ancestors?.length}`] = shopNavigationCategories?.name;

      delete itemCategories.category0;
    }

    return {
      index,
      item_name: product.name!,
      price: (variant.price?.final ?? 0),
      quantity: variant.minimumAmount ?? 1,
      item_id: product.key,
      item_product: product.key,
      item_group: variant?.attributes.wgr,
      item_ekb: variant?.attributes.ekb,
      item_color: variant.attributes.color,
      discount: variant.price?.strike ? (variant.price.strike - variant.price.final).toFixed(2) : 0,
      item_brand: variant.brandName,
      item_variant: variant.attributes.color_number,
      ...itemCategories,
    };
  };

  const pushEvent = <T extends GA4.Event>(data: T) => {
    if (!$gtm) {
      return;
    }

    $gtm.push(data);
  };

  const addPaymentInfo = (paymentMethod: string, products: LineItem[]) => {
    const trackingProducts = products.map(transform);

    pushEvent({
      event: 'add_payment_info',
      ecommerce: {
        currency: $domain.currency,
        payment_type: PAYMENT_MAP[paymentMethod.toLowerCase()] ?? 'Unknown',
        value: trackingProducts.reduce((acc, product) => acc + (product.price * product.quantity), 0),
        items: trackingProducts,
      },
    });
  };

  const addToWishlist = (item: any) => {
    const trackingItem = transformToWishlist(item, 0);

    pushEvent({
      event: 'add_to_wishlist',
      ecommerce: {
        currency: $domain.currency,
        value: trackingItem.price * trackingItem.quantity,
        items: [trackingItem],
      },
    });
  };

  const beginCheckout = (products: LineItem[]) => {
    const trackingProducts = products.map(transform);

    pushEvent({
      event: 'begin_checkout',
      ecommerce: {
        currency: $domain.currency,
        value: trackingProducts.reduce((acc, product) => acc + (product.price * product.quantity), 0),
        items: trackingProducts,
      },
    });
  };

  const removeFromCart = (product: LineItem) => {
    const trackingProduct = transform(product, 0);

    pushEvent({
      event: 'remove_from_cart',
      ecommerce: {
        currency: $domain.currency,
        value: trackingProduct.price * trackingProduct.quantity,
        items: [trackingProduct],
      },
    });
  };

  const viewItemList = (category: Category, products: Product[] | ProductTeaser[]) => {
    const trackingProducts = products.map(transformWeirdProduct);

    pushEvent({
      event: 'view_item_list',
      ecommerce: {
        item_list_id: category?.id ?? 'N/A',
        item_list_name: category?.name ?? 'N/A',
        items: trackingProducts,
      },
    });
  };

  const viewSearch = (searchTerm: string, products: Product[] | ProductTeaser[], filters: any) => {
    const trackingProducts = products.map(transformWeirdProduct);

    pushEvent({
      event: 'view_item_list',
      ecommerce: {
        item_list_name: 'Search Results',
        search_term: searchTerm,
        items: trackingProducts,
        filters,
      },
    });
  };

  const viewReco = (category: Category, products: Product[] | ProductTeaser[], type: string) => {
    const trackingProducts = products.map(transformWeirdProduct);
    setTimeout(() => {
      pushEvent({
        event: 'view_item_list',
        ecommerce: {
          item_list_id: category?.id ?? 'N/A',
          item_list_name: category?.name ?? 'N/A',
          items: trackingProducts,
          filters: {
            RecommenderClientId: type,
            ItemIds: [category?.id],
          },
        },
      });
    }, 500);
  };

  const selectItem = (category?: Category, product?: Product) => {
    if (!product) {
      pushEvent({ event: 'error', errorType: 'tracking - select_item', errorMessage: 'no product provided' });
      return;
    }

    let trackingProduct = transformWeirdProduct(product, 0);

    trackingProduct = {
      ...trackingProduct,
    };

    pushEvent({
      event: 'select_item',
      ecommerce: {
        item_list_id: category?.id ?? 'N/A',
        item_list_name: category?.name ?? 'N/A',
        items: [trackingProduct],
      },
    });
  };

  const viewCart = (cart: Cart) => {
    if (!cart.lineItems) {
      return;
    };

    const trackingProducts = cart.lineItems.map(transform);

    pushEvent({
      event: 'view_cart',
      ecommerce: {
        currency: $domain.currency,
        value: trackingProducts.reduce((acc: number, product) => acc + (product.price * product.quantity), 0),
        items: trackingProducts,
      },
    });
  };

  const purchase = (order: Order) => {
    const items = order.lineItems.map(transform);
    const transactionId = isFunctionalConsentGiven() ? order.orderNumber : `anonym-${Date.now()}`;

    pushEvent({
      event: 'purchase',
      ecommerce: {
        currency: $domain.currency,
        transaction_id: transactionId!,
        tax: Math.round(items.reduce((n, item) => n + (item.price * item.quantity), 0) * (order.taxedPrice?.taxPortions?.[0]?.rate ?? 0) * 1e2) / 1e2,
        value: orderGetters.getFormattedPrice(order.totalPrice),
        shipping: order.shippingInfo?.price ? orderGetters.getFormattedPrice(order.shippingInfo.price) : 0,
        shipping_tier: order.shippingInfo?.shippingMethodName ?? '',
        payment_type: getPaymentMethod(order)!,
        coupon: order.discountCodes?.[0]?.discountCode?.name ?? undefined,
        coupon_key: order.discountCodes?.[0]?.discountCode?.code ?? undefined,
        items,
      } });
  };

  return {
    addPaymentInfo,
    addToWishlist,
    beginCheckout,
    removeFromCart,
    viewItemList,
    viewReco,
    selectItem,
    viewCart,
    purchase,
    viewSearch,

    // Custom
    pushCustomEvent: (event: GA4.CustomType) => pushEvent(event),
  };
};
